import { useLocaleState } from "ra-core";
import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { useWatch } from "react-hook-form";

import {
  parseLabelFormat,
  useRacketOverview,
  type LabelFormat,
} from "./racketData";

// Holder chosen in an ra-core Form (contact_id, company_id)
export const useHolderValues = () => {
  const contactId = useWatch({ name: "contact_id" }) as number | null;
  const companyId = useWatch({ name: "company_id" }) as number | null;
  return { contactId: contactId ?? null, companyId: companyId ?? null };
};

// A day in the app locale, in Rome; "—" when missing
export const useDay = () => {
  const [locale] = useLocaleState();
  return (value?: string | null) =>
    value
      ? new Date(
          value.length === 10 ? `${value}T12:00:00` : value,
        ).toLocaleDateString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          timeZone: "Europe/Rome",
        })
      : "—";
};

// Overdue test rackets of the loans made by a person, for Today
export const useOverdueRackets = (salesId?: number) =>
  useRacketOverview({ lent_by: salesId, is_overdue: true }, salesId != null)
    .data ?? [];

// Sheet of adhesive labels from app_settings (A4 by default)
const LABEL_FORMAT_KEY = "test_racket_label_format";

export const useLabelFormat = (): LabelFormat => {
  const { data } = useQuery({
    queryKey: ["app_settings", LABEL_FORMAT_KEY],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("app_settings")
        .select("value")
        .eq("key", LABEL_FORMAT_KEY)
        .maybeSingle();
      if (error) throw error;
      return data?.value ?? null;
    },
  });
  return parseLabelFormat(data);
};
